// Small in-flight overlay: opened with [B] hotkey, lists drones owned by the
// player's current capital ship and lets them Deploy/Recall each one.
// Purchase is NOT shown here — that lives in the station's DroneBay tab.

import React, { useEffect, useState } from 'react';
import { networkManager } from '../../systems/NetworkManager';

const PALETTE = {
  DARK: '#0f1b26',
  LIGHT: '#f5e8d1',
  TEAL: '#20a5a6',
  ORANGE: '#dd5639',
  DIM: '#4a5a6a',
  PANEL: '#1a2a38',
  GREEN: '#4ade80',
  RED: '#ef4444',
  BORDER: '#2a3a48',
};

interface ShipDrone {
  id: string;
  droneType: string;
  isDeployed: boolean;
  isRogue: boolean;
  hull: number;
  maxHull: number;
}

interface ShipBayOverlayProps {
  onClose: () => void;
}

export function ShipBayOverlay({ onClose }: ShipBayOverlayProps) {
  const [drones, setDrones] = useState<ShipDrone[]>([]);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    networkManager.sendListShipDrones();
    const prevList = networkManager.onShipDronesList;
    networkManager.onShipDronesList = (data) => setDrones(data.drones);
    const prevDeploy = networkManager.onDeployDroneResult;
    networkManager.onDeployDroneResult = (data) => {
      if (!data.success && data.error) setInfo(`⚠ ${data.error}`);
      else setInfo(null);
      networkManager.sendListShipDrones();
    };
    const prevRecall = networkManager.onRecallDroneResult;
    networkManager.onRecallDroneResult = (data) => {
      if (!data.success && data.error) setInfo(`⚠ ${data.error}`);
      else setInfo(null);
      networkManager.sendListShipDrones();
    };
    return () => {
      networkManager.onShipDronesList = prevList;
      networkManager.onDeployDroneResult = prevDeploy;
      networkManager.onRecallDroneResult = prevRecall;
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'b' || e.key === 'B') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      style={{
        position: 'fixed', right: 20, bottom: 100,
        background: PALETTE.DARK, color: PALETTE.LIGHT,
        border: `1px solid ${PALETTE.BORDER}`, borderRadius: 6,
        padding: 12, fontFamily: "'JetBrains Mono', monospace", minWidth: 360,
        zIndex: 999,
      }}
    >
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8,
      }}>
        <span style={{ fontWeight: 600, letterSpacing: '0.08em' }}>SHIP DRONE BAY</span>
        <span style={{ color: PALETTE.DIM, fontSize: 10 }}>[B] or [ESC] to close</span>
      </div>

      {drones.length === 0 && (
        <div style={{ color: PALETTE.DIM, fontSize: 12 }}>
          No drones owned. Buy them at any space station's Drone Bay.
        </div>
      )}

      {drones.map((d) => (
        <div key={d.id} style={{
          display: 'flex', gap: 8, padding: 6, marginBottom: 4,
          background: PALETTE.PANEL, border: `1px solid ${PALETTE.BORDER}`, borderRadius: 4,
          alignItems: 'center', fontSize: 11,
        }}>
          <span style={{ color: PALETTE.TEAL, minWidth: 80 }}>
            {d.droneType.toUpperCase()}
          </span>
          <span style={{ color: PALETTE.DIM, minWidth: 70 }}>
            HP {Math.round(d.hull)}/{d.maxHull}
          </span>
          <span style={{
            minWidth: 70,
            color: d.isRogue ? PALETTE.RED : d.isDeployed ? PALETTE.GREEN : PALETTE.DIM,
          }}>
            {d.isRogue ? 'ROGUE' : d.isDeployed ? 'DEPLOYED' : 'IN BAY'}
          </span>
          {!d.isRogue && (
            d.isDeployed ? (
              <button
                onClick={() => networkManager.sendRecallDrone(d.id)}
                style={{
                  marginLeft: 'auto',
                  padding: '3px 8px', fontSize: 10, fontWeight: 600,
                  background: PALETTE.ORANGE, color: PALETTE.DARK,
                  border: 'none', borderRadius: 3, cursor: 'pointer',
                }}
              >RECALL</button>
            ) : (
              <button
                onClick={() => networkManager.sendDeployDrone(d.id)}
                style={{
                  marginLeft: 'auto',
                  padding: '3px 8px', fontSize: 10, fontWeight: 600,
                  background: PALETTE.TEAL, color: PALETTE.DARK,
                  border: 'none', borderRadius: 3, cursor: 'pointer',
                }}
              >DEPLOY</button>
            )
          )}
        </div>
      ))}

      {info && (
        <div style={{ color: PALETTE.RED, fontSize: 11, marginTop: 6 }}>{info}</div>
      )}
    </div>
  );
}

export default ShipBayOverlay;
